import { useEffect, useRef, useState } from 'react';
import InfluenceArrowsBadge, { influenceBadgeVisible } from './InfluenceArrowsBadge';
import TrendExtensionBadge, { trendExtensionBadgeLook } from './TrendExtensionBadge';
import { setInfluenceArrows } from '../../services/api';

// Operator override for the conjugacy status. Every control saves immediately;
// the badge itself is the confirmation (it round-trips through the socket).
// Reset also happens by changing the level's current wave (backend-side).
const STATUS_OPTIONS = [
  { value: 'conjugacy', label: 'Сопряженность', arrows: 1 },
  { value: 'supercycle', label: 'Суперцикл', arrows: 2, counter: true },
  { value: 'broken', label: '123 (сопряжённость нарушена)', arrows: 3 },
  { value: 'combo_swap', label: 'Смена комбо', arrows: 2 },
  { value: 'limit', label: 'Лимит ×3', arrows: 2 },
  { value: 'config_error', label: 'Ошибка настройки цикла', arrows: 0 },
  { value: 'no_influence', label: 'Нет влияния', arrows: 0 },
  { value: 'reset', label: 'Обнулено', arrows: 0 },
];

// Trend Extension phase of the pair's running wave (MANUAL_PHASES in trend_extension.py).
const TE_PHASES = [
  { value: 'reset', label: '—', title: 'Нет счёта' },
  { value: 'wave1', label: 'w1', title: 'Сопряжённая волна 1' },
  { value: 'wave2', label: 'w2', title: 'Несопряжённая волна 2' },
  { value: 'wave3', label: 'w3', title: 'Волна 3: её конец даёт candidate' },
  { value: 'candidate', label: 'cand', title: 'Candidate: конец текущей волны даёт in play' },
  { value: 'in_play', label: 'in play', title: 'In play: конец текущей волны даёт Over' },
];

const invert = (d) => (d === 'up' ? 'down' : 'up');
const arrowClass = (d) => (d === 'up' ? 'text-emerald-600' : 'text-red-500');
const arrowGlyph = (d) => (d === 'up' ? '▲' : '▼');
const seqOf = (n, first) => Array.from({ length: n }, (_, i) => (i % 2 === 0 ? first : invert(first)));
const optionOf = (value) => STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];

const SECTION = 'text-[10px] font-semibold uppercase tracking-wider text-olive-400';

// Mirrors backend seed_level_state so the grid pictogram is exactly what the panel
// will render after applying — the badge component stays the single source of truth.
const previewState = (o, firstDir, counter) => ({
  status: o.value,
  arrows: o.arrows > 0 ? seqOf(o.arrows, firstDir) : [],
  counter:
    o.value === 'supercycle' ? Number(counter)
      : o.value === 'combo_swap' ? 1
        : o.value === 'limit' ? 4
          : 0,
  swapped: o.value === 'combo_swap',
});

// The pair being edited is this tile's timeframe against one of its ×4 seniors
// (2m has two: 8m and 6m), so both ends are addressed explicitly.
const InfluenceArrowsEditor = ({
  juniorTimeframe, seniorTimeframe, asset, username, timeframe, state,
  // { phase, side, onPick } for the pair's Trend Extension; null elsewhere.
  trendExtension = null,
}) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('conjugacy');
  const [firstDir, setFirstDir] = useState('up');
  const [counter, setCounter] = useState(1);
  const [seriesAlive, setSeriesAlive] = useState(true);
  // Why the last TE pick was not stored; the socket alone would just stay silent.
  const [teError, setTeError] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const rootRef = useRef(null);

  const opt = optionOf(status);
  const arrows = seqOf(opt.arrows, firstDir);

  // "Суперцикл ×1" is the only badge that maps to two different engine states.
  // For ×>=2 the run is necessarily dead, so the flag is not offered.
  const showSeriesToggle = status === 'supercycle' && Number(counter) === 1;

  // Mirror the live socket `state` into the local controls while the menu is
  // closed, so the editor opens on the current status (not stale defaults).
  // Skipped while open to avoid clobbering an in-progress edit.
  useEffect(() => {
    if (open) return;
    const s = state || {};
    setStatus(s.status || 'conjugacy');
    const first = Array.isArray(s.arrows) && s.arrows.length > 0 ? s.arrows[0] : 'up';
    setFirstDir(first === 'down' ? 'down' : 'up');
    if (s.status === 'supercycle') {
      const c = Number(s.counter);
      setCounter(c >= 1 && c <= 3 ? c : 1);
    }
    if (typeof s.series_alive === 'boolean') setSeriesAlive(s.series_alive);
  }, [open, state]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Roll the controls back to what the engine actually holds. Used when a save is
  // rejected, so the menu never keeps showing a status that was not stored.
  const resyncFromState = () => {
    const s = state || {};
    setStatus(s.status || 'conjugacy');
    const first = Array.isArray(s.arrows) && s.arrows.length > 0 ? s.arrows[0] : 'up';
    setFirstDir(first === 'down' ? 'down' : 'up');
    const c = Number(s.counter);
    setCounter(c >= 1 && c <= 3 ? c : 1);
    if (typeof s.series_alive === 'boolean') setSeriesAlive(s.series_alive);
  };

  // Persist on every control interaction, using the value being set right now.
  const save = async (next = {}) => {
    // Nothing to address without both ends of the pair.
    if (!asset || !juniorTimeframe || !seniorTimeframe) return;
    const s = next.status ?? status;
    const dir = next.firstDir ?? firstDir;
    const cnt = next.counter ?? counter;
    const alive = next.seriesAlive ?? seriesAlive;
    const o = optionOf(s);
    setStatusError(null);
    const saved = await setInfluenceArrows({
      symbol: asset.symbol,
      broker: asset.broker,
      // okx (crypto) keys state by username, not asset.account — same convention
      // used everywhere else in TOSPanel for set/toggle calls.
      account: asset.broker === 'okx' ? username : asset.account,
      sectype: asset.sectype,
      strategy: 'tos',
      group: timeframe,
      view: 'solo',
      junior_timeframe: juniorTimeframe,
      senior_timeframe: seniorTimeframe,
      status: s,
      arrows: o.arrows > 0 ? seqOf(o.arrows, dir) : undefined,
      counter: o.counter ? Number(cnt) : undefined,
      series_alive: s === 'supercycle' && Number(cnt) === 1 ? alive : undefined,
      swapped: false,
    });
    // Roll back and say why, so the controls never sit on a status the backend refused.
    if (!saved || saved.error) {
      resyncFromState();
      setStatusError(saved?.error || 'unknown error');
    }
  };

  const pickStatus = (value) => { setStatus(value); save({ status: value }); };
  const flipArrows = () => { const d = invert(firstDir); setFirstDir(d); save({ firstDir: d }); };
  const pickCounter = (n) => { setCounter(n); save({ counter: n }); };
  const toggleSeries = (v) => { setSeriesAlive(v); save({ seriesAlive: v }); };

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        // items-stretch: the TE tile takes the conjugacy tile's height, whatever it is.
        className="inline-flex items-stretch gap-0.5 transition-opacity hover:opacity-80"
        title="Задать статус сопряженности / Trend Extension"
      >
        {trendExtension && (
          <TrendExtensionBadge phase={trendExtension.phase} side={trendExtension.side} />
        )}
        <InfluenceArrowsBadge state={state} />
        {!influenceBadgeVisible(state) && !trendExtensionBadgeLook(trendExtension?.phase, trendExtension?.side) && (
          <span className="text-[10px] text-gray-400" aria-label="Edit indicators">⋯</span>
        )}
      </button>

      {open && (
        <div
          className="absolute mt-1 w-72 rounded-lg border border-olive-200 bg-white shadow-xl"
          style={{ top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 99999 }}
        >
          <div className="border-b border-olive-100 px-3 py-2">
            <div className={SECTION}>
              Сопряженность · {juniorTimeframe} → {seniorTimeframe}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1 p-2">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pickStatus(o.value)}
                title={o.label}
                className={`flex items-center justify-center rounded-md border py-2 transition-colors ${
                  status === o.value
                    ? 'border-olive-400 bg-olive-100'
                    : 'border-transparent hover:bg-olive-50'
                }`}
              >
                <InfluenceArrowsBadge state={previewState(o, firstDir, counter)} preview />
              </button>
            ))}
          </div>
          {statusError && (
            <div className="px-3 pb-2 text-[10px] leading-snug text-red-500" role="alert">
              Статус не сохранён: {statusError}
            </div>
          )}

          {(opt.arrows > 0 || opt.counter || showSeriesToggle) && (
            <div className="space-y-2 border-t border-olive-100 px-3 py-2">
              {opt.arrows > 0 && (
                <div>
                  <div className={`${SECTION} mb-1`}>Направление</div>
                  <button
                    type="button"
                    onClick={flipArrows}
                    className="inline-flex items-center gap-0.5 rounded-md border border-olive-200 bg-white px-2 py-1 leading-none transition-colors hover:bg-olive-50"
                  >
                    {arrows.map((d, i) => (
                      <span key={i} className={`text-[13px] ${arrowClass(d)}`}>{arrowGlyph(d)}</span>
                    ))}
                  </button>
                </div>
              )}

              {opt.counter && (
                <div>
                  <div className={`${SECTION} mb-1`}>Счётчик циклов</div>
                  <div className="flex gap-1">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => pickCounter(n)}
                        className={`flex-1 rounded py-1 text-[10px] font-semibold transition-all duration-200 ${
                          Number(counter) === n
                            ? 'bg-olive-600 text-white shadow-sm'
                            : 'text-olive-500 hover:bg-olive-50 hover:text-olive-700'
                        }`}
                      >
                        ×{n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showSeriesToggle && (
                <label className="flex cursor-pointer items-start gap-2 rounded-md bg-olive-50 px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={seriesAlive}
                    onChange={(e) => toggleSeries(e.target.checked)}
                    className="mt-0.5 accent-olive-600"
                  />
                  <span className="leading-tight">
                    <span className="block text-[11px] font-medium text-olive-700">
                      Были последовательные перевороты
                    </span>
                    <span className="mt-0.5 block text-[9px] leading-snug text-olive-400">
                      Переворот старшего на текущей волне приведет к статусу «
                      {seriesAlive ? 'Сопряженность нарушена' : 'Сопряженность'}»
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}

          {trendExtension && (
            <div className="border-t border-olive-100 px-3 py-2">
              <div className={`${SECTION} mb-1`}>Trend Extension</div>
              <div className="flex gap-1">
                {TE_PHASES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={async () => {
                      setTeError(null);
                      const res = await trendExtension.onPick(p.value);
                      if (res?.error) setTeError(res.error);
                    }}
                    title={p.title}
                    className={`flex-1 whitespace-nowrap rounded py-1 text-[10px] font-semibold transition-all duration-200 ${
                      trendExtension.phase === p.value
                        ? 'bg-olive-600 text-white shadow-sm'
                        : 'text-olive-500 hover:bg-olive-50 hover:text-olive-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {teError && (
                <div className="mt-1 text-[10px] leading-snug text-red-500" role="alert">
                  TE не сохранён: {teError}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InfluenceArrowsEditor;
